/**
 * billToConfirmation.ts — R4 (SPEC invoice-identity-cascade) — AC4 + AC8 + AC10c.
 *
 * The EVIDENTIARY post-save confirmation — NOT promissory. After a cascade Save,
 * the confirmation NAMES the sibling invoices that changed NOW, generated from
 * the ACTUAL saved value + the ACTUAL changed sibling NUMBERS the backend
 * returned (R3's retroactive apply / cascade response). Invoice numbers are
 * Christa's native language; they are rendered VERBATIM, bracketed ("[0607]").
 *
 * AC8: a Save is visibly TERMINAL — there is no silent no-op path. Every Save
 * resolves to EITHER an evidentiary success confirmation OR an inline error.
 * `buildSaveResult` is the single classifier the parent uses so a 0-row /
 * unchanged outcome still produces a user-visible state change.
 */

export interface SaveResultSuccess {
  kind: 'success';
  message: string;
  /** The affected sibling numbers, VERBATIM (for chip rendering). */
  affectedNumbers: string[];
}

export interface SaveResultError {
  kind: 'error';
  message: string;
}

export type SaveResult = SaveResultSuccess | SaveResultError;

/**
 * Build the evidentiary success confirmation (AC4 / AC10c).
 *
 * @param savedValue       the value that was actually saved (e.g. the DBA name)
 * @param level            the cascade level the write landed at
 * @param projectName      project display name (project-level copy)
 * @param clientName       client display name (client-level copy)
 * @param affectedNumbers  the sibling invoice NUMBERS that changed NOW, from the
 *                         backend response (VERBATIM). Empty ⇒ only this invoice.
 */
export function buildEvidentiaryConfirmation(args: {
  savedValue: string;
  level: 'this_invoice' | 'this_project' | 'this_client';
  projectName?: string | null;
  clientName?: string | null;
  affectedNumbers: string[];
}): SaveResultSuccess {
  const { savedValue, level, projectName, clientName, affectedNumbers } = args;
  const value = savedValue.trim() || 'the new value';

  if (level === 'this_invoice') {
    // "Just this invoice": only this invoice changed. Terminal + truthful.
    return {
      kind: 'success',
      message: `Saved. This invoice now bills as ${value}.`,
      affectedNumbers: [],
    };
  }

  if (affectedNumbers.length === 0) {
    // B2 going-forward: the current invoice is fixed AND the future default is
    // set, but NO existing sibling drafts were rewritten (they keep their own
    // identities). Confirm the current invoice + the future-inherit promise
    // WITHOUT claiming any other existing invoice changed.
    const where =
      level === 'this_project'
        ? (projectName?.trim() || 'this project')
        : (clientName?.trim() || 'this client');
    return {
      kind: 'success',
      message: `Saved. This invoice and future ${where} invoices will bill as ${value}.`,
      affectedNumbers: [],
    };
  }

  const where =
    level === 'this_project'
      ? (projectName?.trim() || 'this project')
      : (clientName?.trim() || 'this client');
  const n = affectedNumbers.length;
  // "The 2 other {where} drafts" / "The 1 other {where} draft".
  const noun = n === 1 ? `other ${where} draft` : `other ${where} drafts`;
  const verb = n === 1 ? 'now bills' : 'now bill';
  const list = affectedNumbers.map((num) => `[${String(num).trim()}]`).join(' ');

  return {
    kind: 'success',
    // "Saved. The 2 other Marine Landing drafts now bill as X — [0607] [0611]"
    message: `Saved. The ${n} ${noun} ${verb} as ${value} — ${list}`,
    affectedNumbers,
  };
}

/**
 * Classify a Save outcome into a TERMINAL, user-visible result (AC8). There is
 * no third "nothing happened" branch — an apparently-unchanged outcome is still
 * surfaced as a success ("Already set to X.") so the user always sees terminal
 * state.
 */
export function buildSaveResult(args: {
  ok: boolean;
  changed: boolean;
  savedValue: string;
  level: 'this_invoice' | 'this_project' | 'this_client';
  projectName?: string | null;
  clientName?: string | null;
  affectedNumbers: string[];
  errorMessage?: string;
}): SaveResult {
  if (!args.ok) {
    return {
      kind: 'error',
      message:
        args.errorMessage?.trim() ||
        "That didn't save. Nothing was changed — please try again.",
    };
  }
  if (!args.changed) {
    const value = args.savedValue.trim() || 'the current value';
    return {
      kind: 'success',
      message: `Already set to ${value}. No change needed.`,
      affectedNumbers: [],
    };
  }
  return buildEvidentiaryConfirmation({
    savedValue: args.savedValue,
    level: args.level,
    projectName: args.projectName,
    clientName: args.clientName,
    affectedNumbers: args.affectedNumbers,
  });
}
